import { Localized } from "@fluent/react/compat";
import cn from "classnames";
import { MediaContainer } from "coral-admin/components/MediaContainer";
import ImageModal from "coral-admin/components/MediaContainer/ImageModal";
import { HOTKEYS } from "coral-admin/constants";
import { Markdown } from "coral-framework/components";
import { useCoralContext } from "coral-framework/lib/bootstrap";
import { GQLTAG, GQLWordlistMatch } from "coral-framework/schema";
import { PropTypesOf } from "coral-framework/types";
import {
  Button,
  ButtonIcon,
  Card,
  Flex,
  HorizontalGutter,
  Icon,
  TextLink,
  Timestamp,
} from "coral-ui/components/v2";
import key from "keymaster";
import { noop } from "lodash";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CommentContainer_comment as CommentData } from "coral-stream/__generated__/CommentContainer_comment.graphql";
import { CommentContent, InReplyTo, UsernameButton } from "../Comment";
import ApproveButton from "./ApproveButton";
import CommentAuthorContainer from "./CommentAuthorContainer";
import ImageDetailsContainer from "./ImageDetailsContainer";
import RatingDisplay from "./RatingDisplay";
import Markers from "./Markers";
import MarkersContainer from "./MarkersContainer";
import RejectButton from "./RejectButton";
import TagButton from "./TagButton";
import TagCommentModal from "./TagCommentModal";

import NotAvailable from "coral-admin/components/NotAvailable";
import styles from "./ModerateCard.css";

interface ReviewImage {
  id: string;
  url: string;
  status: string;
  reasons?: string[];
}
interface Props {
  id: string;
  username: string;
  createdAt: string;
  body: string;
  highlight?: boolean;
  inReplyTo?: {
    id: string;
    username: string | null;
    name?: string | null;
  } | null;
  comment: PropTypesOf<typeof MarkersContainer>["comment"] &
    PropTypesOf<typeof CommentAuthorContainer>["comment"] &
    PropTypesOf<typeof MediaContainer>["comment"] & {
      parent?: CommentData["parent"];
      site: {
        id: string;
      };
    };
  settings: PropTypesOf<typeof MarkersContainer>["settings"] & {
    enableQaFlag?: boolean;
    qaFlagEnabledSites?: string[];
  };
  status: "approved" | "rejected" | "undecided";
  featured: boolean;
  qaTagged: boolean;
  moderatedBy: React.ReactNode | null;
  viewContextHref: string;
  showStory: boolean;
  meta?: {
    storyLink?: string | null;
    hed?: string | null;
  } | null;
  storyHref?: string;
  siteName: string | null;
  onModerateStory?: React.EventHandler<React.MouseEvent>;
  onApprove: () => void;
  onReject: () => void;
  onImageApprove: (imageID: string) => void;
  onImageReject: (imageID: string) => void;
  onFeature: (tag: GQLTAG, label?: string) => void;
  onUsernameClick: (id?: string) => void;
  onConversationClick: (() => void) | null;
  onFocusOrClick: () => void;
  mini?: boolean;
  hideUsername?: boolean;
  selected?: boolean;
  /**
   * If set to true, it means this comment is about to be removed
   * from the queue. This will trigger some styling changes to
   * reflect that
   */
  dangling?: boolean;
  deleted?: boolean;

  /**
   * If set to true, it means that this comment cannot be moderated by the
   * current user.
   */
  readOnly?: boolean;
  edited: boolean;
  selectPrev?: () => void;
  selectNext?: () => void;
  onBan: () => void;
  isQA?: boolean;
  rating?: number | null;
  ratingScale?: number | null;

  bannedWords?: Readonly<Readonly<GQLWordlistMatch>[]>;
  suspectWords?: Readonly<Readonly<GQLWordlistMatch>[]>;
  isArchived?: boolean;
  isArchiving?: boolean;
  images: ReviewImage[];
  commentTagLabel?: {
    FEATURED: string | null;
    QA: string | null;
  } | null;
}

const ModerateCard: FunctionComponent<Props> = ({
  id,
  username,
  createdAt,
  body,
  highlight = false,
  inReplyTo,
  comment,
  settings,
  rating,
  ratingScale,
  viewContextHref,
  status,
  featured,
  qaTagged,
  onApprove,
  onReject,
  onImageApprove,
  onImageReject,
  onFeature,
  onUsernameClick,
  dangling,
  showStory,
  meta,
  storyHref,
  onModerateStory,
  siteName,
  moderatedBy,
  selected,
  onFocusOrClick,
  onConversationClick,
  mini = false,
  hideUsername = false,
  deleted = false,
  readOnly = false,
  edited,
  selectNext,
  selectPrev,
  onBan,
  isQA,
  bannedWords,
  suspectWords,
  isArchived,
  isArchiving,
  images,
  commentTagLabel,
}) => {
  const div = useRef<HTMLDivElement>(null);
  const { window } = useCoralContext();

  // Get the Spectra URL based on hostname
  const spectraURL = useMemo(() => {
    const hostname = window.location.hostname;
    const isProduction = hostname === "coral.condenastdigital.com";

    return isProduction
      ? "https://spectra.conde.digital"
      : "https://stg-spectra.conde.digital";
  }, [window.location.hostname]);

  const [selectedImageState, setSelectedImageState] = useState<{
    url: string;
    alt: string;
  } | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const handleImageClick = (imageURL: string, imageAlt: string) => {
    setSelectedImageState({
      url: imageURL,
      alt: imageAlt,
    });
    setModalOpen(true);
  };

  const handleImageModalClose = () => {
    setModalOpen(false);
    setSelectedImageState(null);
  };

  const handleTagModalClose = () => {
    setTagModalOpen(false);
  };

  const enableQaFlag = () => {
    if (!settings?.enableQaFlag) {
      return false;
    }
    if (settings?.enableQaFlag && !settings.qaFlagEnabledSites?.length) {
      return true;
    }
    return settings?.qaFlagEnabledSites?.includes(comment?.site?.id);
  };

  const handleTagClick = useCallback(() => {
    if (featured || qaTagged) {
      onFeature(featured ? GQLTAG.FEATURED : GQLTAG.QA);
    } else {
      if (!enableQaFlag()) {
        onFeature(GQLTAG.FEATURED);
        return;
      }
      setTagModalOpen(true);
    }
  }, [featured, qaTagged, onFeature]);

  const handleTagSubmit = useCallback(
    (tag: GQLTAG, label: string) => {
      onFeature(tag, label);
      setTagModalOpen(false);
    },
    [onFeature]
  );

  useEffect(() => {
    if (selected) {
      if (selectNext) {
        key(HOTKEYS.NEXT, id, selectNext);
      }
      if (selectPrev) {
        key(HOTKEYS.PREV, id, selectPrev);
      }
      if (onBan) {
        key(HOTKEYS.BAN, id, onBan);
      }
      key(HOTKEYS.APPROVE, id, onApprove);
      key(HOTKEYS.REJECT, id, onReject);

      // The the scope such that only events attached to the ${id} scope will
      // be honored.
      key.setScope(id);

      return () => {
        // Remove all events that are set in the ${id} scope.
        key.deleteScope(id);
      };
    } else {
      // Remove all events that were set in the ${id} scope.
      key.deleteScope(id);
    }

    return noop;
  }, [selected, id, selectNext, selectPrev, onBan, onApprove, onReject]);

  useEffect(() => {
    if (selected && div && div.current) {
      div.current.focus();
    }
  }, [selected, div]);

  const commentBody = useMemo(
    () =>
      deleted ? (
        <Localized id="moderate-comment-deleted-body">
          <div className={styles.deleted}>
            This comment is no longer available. The commenter has deleted their
            account.
          </div>
        </Localized>
      ) : (
        body
      ),
    [deleted, body]
  );

  const commentAuthorClick = useCallback(() => {
    onUsernameClick();
  }, [onUsernameClick]);

  const commentParentAuthorClick = useCallback(() => {
    if (inReplyTo?.id) {
      onUsernameClick(inReplyTo.id);
    }
  }, [onUsernameClick, inReplyTo]);

  const isReply = Boolean(comment.parent);
  const featureBtnHoverText = isReply
    ? "Replies can’t be pinned"
    : "Pin This Comment";

  return (
    <>
      <Card
        className={cn(
          styles.root,
          { [styles.borderless]: mini },
          { [styles.dangling]: dangling },
          { [styles.deleted]: deleted },
          { [styles.selected]: selected }
        )}
        ref={div}
        tabIndex={0}
        data-testid={`moderate-comment-${id}`}
        id={`moderate-comment-${id}`}
        onClick={onFocusOrClick}
      >
        <Flex>
          <div className={styles.mainContainer}>
            <div
              className={cn(styles.topBar, {
                [styles.topBarMini]: mini && !inReplyTo,
              })}
            >
              <Flex alignItems="center">
                {!hideUsername && username && (
                  <>
                    <UsernameButton
                      username={username}
                      onClick={commentAuthorClick}
                    />
                    <CommentAuthorContainer comment={comment} />
                  </>
                )}
                <Timestamp className={styles.timestamp}>{createdAt}</Timestamp>
                {edited && (
                  <Localized id="moderate-comment-edited">
                    <span className={styles.edited}>(edited)</span>
                  </Localized>
                )}
                <TagButton
                  onClick={handleTagClick}
                  enabled={!deleted && !isQA && !readOnly && !isReply}
                  featured={featured}
                  qaTagged={qaTagged}
                  title={featureBtnHoverText}
                  enableQaFlag={enableQaFlag()}
                />
              </Flex>
              {inReplyTo && (
                <InReplyTo onUsernameClick={commentParentAuthorClick}>
                  {inReplyTo}
                </InReplyTo>
              )}
            </div>
            {rating !== null && rating !== undefined && (
              <div className={styles.ratingsArea}>
                <RatingDisplay rating={rating} ratingScale={ratingScale} />
              </div>
            )}
            <div className={styles.contentArea}>
              <div className={styles.content}>
                <CommentContent
                  highlight={highlight}
                  bannedWords={bannedWords}
                  suspectWords={suspectWords}
                  preserveWhitespace={!deleted}
                >
                  {commentBody}
                </CommentContent>
                <MediaContainer comment={comment} />
              </div>
              <div className={styles.content}>
                <div className={styles.imageGrid}>
                  {images &&
                    images.map((image: any) => (
                      <div key={image.id} className={styles.imageItem}>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                        <div
                          onClick={() =>
                            handleImageClick(
                              `${spectraURL}/eu-central-1/api/v2/optimize?format=type_jpeg&url=${encodeURIComponent(
                                image.url
                              )}`,
                              image.status
                            )
                          }
                          className={styles.imageItem}
                          role="button"
                          tabIndex={0}
                        >
                          <img
                            src={`${spectraURL}/eu-central-1/api/v2/optimize?format=type_jpeg&url=${encodeURIComponent(
                              image.url
                            )}`}
                            width={80}
                            height={80}
                            alt={image.status}
                          />
                        </div>
                        <div className={styles.imageActions}>
                          <div className={styles.imageButtons}>
                            <RejectButton
                              onClick={() => onImageReject(image.id)}
                              invert={image.status === "rejected"}
                              disabled={image.status === "rejected"}
                              readOnly={readOnly}
                              className={styles.imageActionButton}
                            />
                            <ApproveButton
                              onClick={() => onImageApprove(image.id)}
                              invert={image.status === "approved"}
                              disabled={image.status === "approved"}
                              readOnly={readOnly}
                              className={styles.imageActionButton}
                            />
                          </div>
                          <div className={styles.moderationReasons}>
                            <Markers
                              details={
                                <ImageDetailsContainer
                                  status={image.status}
                                  reasons={image.reasons}
                                />
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              {onConversationClick && (
                <div className={styles.viewContext}>
                  <Button iconLeft variant="text" onClick={onConversationClick}>
                    <ButtonIcon>question_answer</ButtonIcon>
                    <Localized id="moderate-comment-viewConversation">
                      <span>View conversation</span>
                    </Localized>
                  </Button>
                </div>
              )}
              <div
                className={cn(styles.separator, {
                  [styles.ruledSeparator]: !mini,
                })}
              />
            </div>
            <div className={styles.footer}>
              <HorizontalGutter spacing={3}>
                {showStory && (
                  <div>
                    <div className={styles.storyLabel}>
                      <Localized id="moderate-comment-storyLabel">
                        <span>Comment on</span>
                      </Localized>
                      <span>:</span>
                    </div>
                    <div className={styles.commentOn}>
                      {siteName && (
                        <span className={styles.siteName}>
                          {siteName}
                          <Icon>keyboard_arrow_right</Icon>
                        </span>
                      )}
                      <span className={styles.meta}>
                        {meta?.storyLink ? (
                          <a href={meta?.storyLink}>
                            <Markdown>{meta.hed}</Markdown>
                          </a>
                        ) : (
                          <NotAvailable />
                        )}
                      </span>
                    </div>
                    <div>
                      <Localized id="moderate-comment-moderateStory">
                        <TextLink
                          href={storyHref}
                          onClick={onModerateStory}
                          className={styles.link}
                        >
                          Moderate Story
                        </TextLink>
                      </Localized>
                    </div>
                  </div>
                )}
                <MarkersContainer
                  onUsernameClick={onUsernameClick}
                  comment={comment}
                  settings={settings}
                />
              </HorizontalGutter>
            </div>
          </div>
          <Flex
            className={cn(styles.aside, {
              [styles.asideWithoutReplyTo]: !inReplyTo,
              [styles.asideMini]: mini && !inReplyTo,
              [styles.asideMiniWithReplyTo]:
                mini && inReplyTo && inReplyTo.username,
            })}
            alignItems="center"
            direction="column"
            itemGutter
          >
            {!mini && (
              <Localized id="moderate-comment-decision">
                <div className={styles.decision}>DECISION</div>
              </Localized>
            )}
            <Flex itemGutter>
              <RejectButton
                onClick={onReject}
                invert={status === "rejected"}
                disabled={
                  status === "rejected" ||
                  dangling ||
                  deleted ||
                  readOnly ||
                  isArchived ||
                  isArchiving
                }
                readOnly={readOnly}
                className={cn({
                  [styles.miniButton]: mini,
                })}
              />
              <ApproveButton
                onClick={onApprove}
                invert={status === "approved"}
                disabled={
                  status === "approved" ||
                  dangling ||
                  deleted ||
                  readOnly ||
                  isArchived ||
                  isArchiving
                }
                readOnly={readOnly}
                className={cn({
                  [styles.miniButton]: mini,
                })}
              />
            </Flex>
            {moderatedBy}
          </Flex>
        </Flex>
      </Card>
      {selectedImageState?.url && (
        <ImageModal
          open={modalOpen}
          onClose={handleImageModalClose}
          imageURL={selectedImageState.url}
          imageAlt={selectedImageState.alt}
        />
      )}
      {tagModalOpen && (
        <TagCommentModal
          open={tagModalOpen}
          onClose={handleTagModalClose}
          onSubmit={handleTagSubmit}
          commentTagLabel={commentTagLabel}
        />
      )}
    </>
  );
};

export default ModerateCard;
